import { useNavigate, useParams } from 'react-router-dom';
import { DT, DS2 } from '../../theme/designTokens';
import { useFriends } from '../../context/FriendContext';
import { useIsMobile } from '../../hooks/useBreakpoint';
import { useTranslation } from '../../i18n/react';
import { ConversationList } from './ConversationList';
import { MessageThread } from './MessageThread';

function ChatEmptyIcon() {
  return (
    <svg width={56} height={56} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H9l-4.5 4v-4H6.5" stroke="#4A4A4A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ThreadEmptyState({ message, onBack }: { message: string; onBack?: () => void }) {
  const { t } = useTranslation('messages');
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: DT.color.pageBg }}>
      {onBack && (
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${DS2.color.cardBorder}` }}>
          <button type="button" onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FFFFFF', fontFamily: DS2.font.body, fontSize: 13, cursor: 'pointer', padding: 0 }}>
            {t('back')}
          </button>
        </div>
      )}
      <div style={{ margin: 'auto', textAlign: 'center', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <ChatEmptyIcon />
        </div>
        <p style={{ margin: 0, fontFamily: DS2.font.body, fontSize: 14, color: '#8A8A8A', maxWidth: 280 }}>
          {message}
        </p>
      </div>
    </div>
  );
}

export function MessagesScreen() {
  const isMobile = useIsMobile();
  const { t } = useTranslation('messages');
  const navigate = useNavigate();
  const { friendId: friendIdParam } = useParams<{ friendId?: string }>();
  const { conversations, friends } = useFriends();

  const parsedId = friendIdParam ? Number(friendIdParam) : NaN;
  const activeFriendId = Number.isFinite(parsedId) ? parsedId : null;

  const friendInfo = activeFriendId != null
    ? conversations.find(c => c.friend.id === activeFriendId)?.friend
      ?? friends.find(f => f.friend.id === activeFriendId)?.friend
      ?? null
    : null;

  function handleSelect(id: number) {
    navigate(`/messages/${id}`);
  }
  function handleBack() {
    navigate('/messages');
  }

  const showList   = !isMobile || activeFriendId == null;
  const showThread = !isMobile || activeFriendId != null;

  return (
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {showList && (
          <ConversationList activeFriendId={activeFriendId} onSelect={handleSelect} fullWidth={isMobile} />
        )}
        {showThread && (
          activeFriendId != null && friendInfo ? (
            <MessageThread
              key={activeFriendId}
              friendId={activeFriendId}
              friendName={friendInfo.display_name}
              friendCountryCode={friendInfo.country_code}
              friendAvatarKey={friendInfo.avatar_key}
              onBack={isMobile ? handleBack : undefined}
            />
          ) : activeFriendId != null ? (
            <ThreadEmptyState message={t('notFriend')} onBack={isMobile ? handleBack : undefined} />
          ) : (
            <ThreadEmptyState message={t('selectConversation')} />
          )
        )}
      </div>
    </div>
  );
}
